import { ability } from '@oliasoft/authorization-ui';
import { useEffect, useState } from 'react';
import { RouteComponentProps, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import { usePkce } from '@oliasoft/pkce';
import { Page } from '@oliasoft-open-source/react-ui-library';

import { getUserPermissions } from '~modules/Auth/api';
import user from '~modules/User';
import {
  canCreateCustomerInternal, canCreateLicenseInternal, canCreateProductInternal, canCreateRoleInternal, canCreateFeatureFlagInternal, canDeleteCustomerInternal,
  canDeleteIdPUserInternal, canDeleteLicenseInternal, canDeleteProductInternal, canDeleteRoleInternal, canReadCustomer, canReadCustomerInternal,
  canReadIdentificationsInternal, canReadLicenseInternal, canReadOrganizationsInternal, canReadProductsInternal, canReadRoleForApplicationInternal,
  canReadRolePermissionInternal, canReadStats, canReadSubjectsInternal, canSynchronizeRoleInternal, canUpdateIdPUserInternal, canUpdateLicenseInternal,
  canUpdateProductInternal, canUpdateRoleInternal,
  canAssignLicenseToUser, canAssignRoleToUser, canAssignRoleToUserInternal,
  canCreateCustomerExternalUsers,
  canDeleteCustomerExternalUsers, canReadLicenseUsers, canRemoveLicenseFromUser, canRemoveRoleFromUser, canReadExternalUsersRequestsInternal,
  canReadFeatureFlags, canCreateSubjectsInternal, canEditFeatureFlags, canDeleteFeatureFlags,
  canReadSurveysInternal, canCreateSurveysInternal, canUpdateSurveysInternal, canDeleteSurveysInternal,
  canReadQuestionsInternal, canCreateQuestionsInternal, canUpdateQuestionsInternal, canDeleteQuestionsInternal,
  canCreateUserSurveyInternal, canDeleteUserSurveyInternal,
  canReadReleaseNotesInternal, canCreateReleaseNotesInternal, canUpdateReleaseNotesInternal, canDeleteReleaseNotesInternal,
} from '~permissions';
import { NAVIGATION_CONSTANTS } from '../../constants';
import Header from '../Header';
import { PrivateRoute } from '../../HOCS/withRouteProtection';
import * as routes from '../../routes';
import { loadLocalStoreData, saveLocalStoreData } from '../../services/local-storage.service';
import './index.css';

const licensesAdministrationScreenPermissions = {
  canReadLicense: canReadLicenseInternal,
  canCreateLicense: canCreateLicenseInternal,
  canCreateProduct: canCreateProductInternal,
  canReadRolePermission: canReadRolePermissionInternal,
  canUpdateProduct: canUpdateProductInternal,
  canDeleteProduct: canDeleteProductInternal,
  canUpdateLicense: canUpdateLicenseInternal,
  canDeleteLicense: canDeleteLicenseInternal,
  canUpdateProducts: canUpdateProductInternal,
  canReadProducts: canReadProductsInternal,
  canReadRoles: canReadRoleForApplicationInternal,
  canReadSubjects: canReadSubjectsInternal,
  canReadStats,
  canReadFeatureFlags,
  canReadReleaseNotes: canReadReleaseNotesInternal,
};

const rolesAdministrationScreenPermissions = {
  canReadRoles: canReadRoleForApplicationInternal,
  canDeleteRole: canDeleteRoleInternal,
  canEditRole: canUpdateRoleInternal,
  canUpdateRole: canDeleteRoleInternal,
  canCreateRole: canCreateRoleInternal,
  canSynchronizeRole: canSynchronizeRoleInternal,
};

const subjectAdministrationScreenPermissions = {
  canReadSubjects: canReadSubjectsInternal,
  canCreateSubjects: canCreateSubjectsInternal
};

const featureFlagAdministrationScreenPermissions = {
  canReadFeatureFlags,
  canCreateFeatureFlags: canCreateFeatureFlagInternal,
  canDeleteFeatureFlags,
  canEditFeatureFlags
};

const IdentificationScreenPermissions = {
  canReadIdentificationsInternal,
  canUpdateIdPUserInternal,
  canDeleteIdPUserInternal
};

const SettingsScreenPermissions = {
  canAssignLicenseToUser,
  canRemoveLicenseFromUser,
  canReadLicenseUsers,
  canAssignRoleToUser,
  canAssignInternalRoleToUser: canAssignRoleToUserInternal,
  canRemoveRoleFromUser,
  canCreateCustomerExternalUsers,
  canDeleteCustomerExternalUsers
};

const externalUsersRequestPermissions = {
  canReadExternalUsersRequestsInternal,
};

const surveysPermissions = {
  canReadSurveysInternal,
  canCreateSurveysInternal,
  canUpdateSurveysInternal,
  canDeleteSurveysInternal,
  canReadQuestionsInternal,
  canCreateQuestionsInternal,
  canUpdateQuestionsInternal,
  canDeleteQuestionsInternal,
  canCreateUserSurveyInternal,
  canDeleteUserSurveyInternal,
};

const releaseNotesPermissions = {
  canReadReleaseNotesInternal,
  canCreateReleaseNotesInternal,
  canUpdateReleaseNotesInternal,
  canDeleteReleaseNotesInternal,
};

// Setting default abilities from localstorage before new abilities are taken (avoid issue with F5)
const preAbilities = loadLocalStoreData('permissions', [], true);
ability.update(preAbilities);

export interface IMainRoutesContainerProps extends Partial<RouteComponentProps> {
  history: RouteComponentProps['history'];
  userLoginToken: (accessToken: string, history: RouteComponentProps['history']) => Promise<unknown>;
}

/** Main app routes, most of them are internal level access. */
const MainRoutesContainer = (props: IMainRoutesContainerProps) => {
  const { history } = props;
  const auth = usePkce();
  const accessToken = auth?.accessToken;
  const [abilitiesLoaded, setAbilitiesLoaded] = useState(false);

  const initMainRoutes = async () => {
    if (accessToken) {
      await props.userLoginToken(accessToken, history);
      const { data: abilities } = await getUserPermissions();
      saveLocalStoreData('permissions', abilities, true);
      ability.update(abilities);
      setAbilitiesLoaded(true);

      if (ability.cannot(canReadIdentificationsInternal.action, canReadIdentificationsInternal.subject)) {
        history.push(NAVIGATION_CONSTANTS.COMPANY_ADMINISTRATION_ACCOUNT_ROUTE);
      }
    }
  }

  useEffect(() => {
    initMainRoutes();
  }, [accessToken]);

  if (!abilitiesLoaded) {
    return null;
  }
  return (
    <Page left={0} padding>
      <Header />
      <div className="container-layout">
        <Switch>
          <PrivateRoute
            canNavigate={rolesAdministrationScreenPermissions.canReadRoles}
            permissions={rolesAdministrationScreenPermissions}
            path={`${NAVIGATION_CONSTANTS.ROLE_ADMINISTRATION_ROUTE}/:applicationId?`}
            component={routes.RolesAdministrationScreen}
          />
          <PrivateRoute
            path={NAVIGATION_CONSTANTS.COMPANY_CREATION_ROUTE}
            canNavigate={canCreateCustomerInternal}
            permissions={{ canCreateCustomerInternal }}
            component={routes.CompanyRegistrationScreen}
          />
          <PrivateRoute
            path={`${NAVIGATION_CONSTANTS.STATISTICS_ROUTE}/:applicationId?`}
            component={routes.StatisticsScreen}
          />
          <PrivateRoute
            path={NAVIGATION_CONSTANTS.LICENSE_ADMINISTRATION_ROUTE}
            canNavigate={canReadLicenseInternal}
            permissions={licensesAdministrationScreenPermissions}
            component={routes.LicensesAdministrationScreen}
          />
          <PrivateRoute
            path={NAVIGATION_CONSTANTS.COMPANY_ADMINISTRATION_ROUTE}
            canNavigate={canReadCustomer}
            permissions={{ canReadCustomer }}
            component={routes.CompanyAdministrationScreen}
          />
          <PrivateRoute
            path={`${NAVIGATION_CONSTANTS.CUSTOMER_ADMINISTRATION_ROUTE}/:customerId?`}
            canNavigate={canReadOrganizationsInternal}
            permissions={{ canReadCustomerInternal, canDeleteCustomerInternal }}
            component={routes.CompaniesOverviewScreen}
          />
          <PrivateRoute
            path={NAVIGATION_CONSTANTS.IDENTIFICATION_ROUTE}
            canNavigate={canReadIdentificationsInternal}
            permissions={IdentificationScreenPermissions}
            component={routes.IdentificationScreen}
          />
          <PrivateRoute
            path={`${NAVIGATION_CONSTANTS.SUBJECT_ADMINISTRATION_ROUTE}/:applicationId?`}
            permissions={subjectAdministrationScreenPermissions}
            component={routes.SubjectAdministrationScreen}
            canNavigate
          />
          <PrivateRoute
            path={`${NAVIGATION_CONSTANTS.FEATURE_FLAGS_ADMINISTRATION_ROUTE}/:applicationId?`}
            permissions={featureFlagAdministrationScreenPermissions}
            component={routes.FeatureFlagsAdministrationScreen}
            canNavigate
          />
          <PrivateRoute
            path={`${NAVIGATION_CONSTANTS.PAYLOAD_COMPARISON_ROUTE}`}
            // TODO: Set permissions and canNavigate
            permissions={subjectAdministrationScreenPermissions}
            canNavigate
            component={routes.PayloadComparisonScreen}
          />
          <PrivateRoute
            path={`${NAVIGATION_CONSTANTS.EXTERNAL_USERS_REQUESTS_ROUTE}`}
            permissions={externalUsersRequestPermissions.canReadExternalUsersRequestsInternal}
            canNavigate={externalUsersRequestPermissions.canReadExternalUsersRequestsInternal}
            component={routes.ExternalUsersRequestsScreen}
          />
          <PrivateRoute
            path={`${NAVIGATION_CONSTANTS.SURVEY_ROUTE}/:surveyId?`}
            permissions={surveysPermissions}
            canNavigate={surveysPermissions.canReadSurveysInternal}
            component={routes.SurveyScreen}
          />
          <PrivateRoute
            path={`${NAVIGATION_CONSTANTS.SURVEYS_ROUTE}`}
            permissions={surveysPermissions}
            canNavigate={surveysPermissions.canReadSurveysInternal}
            component={routes.SurveysScreen}
          />
          <PrivateRoute
            path={`${NAVIGATION_CONSTANTS.RELEASE_NOTES_ROUTE}/:productId?`}
            permissions={releaseNotesPermissions}
            canNavigate={releaseNotesPermissions.canReadReleaseNotesInternal}
            component={routes.ReleaseNotesScreen}
          />
          <PrivateRoute
            path="/test-sentry"
            canNavigate
            permissions={SettingsScreenPermissions}
            component={routes.TestSentryScreen}
          />
          <PrivateRoute
            path="/"
            canNavigate
            permissions={SettingsScreenPermissions}
            component={routes.SettingsScreen}
          />
        </Switch>
      </div>
    </Page>
  );
};

const mapDispatchToProps = {
  userLoginToken: user.actions.userLoginToken,
};

export default connect(null, mapDispatchToProps)(MainRoutesContainer);
